import { useState, useEffect } from "react";

// Tailwind's default breakpoints
const breakpoints = {
    xs: 480,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    "2xl": 1536,
};

function resolveBreakpointValue(value) {
    if (typeof value === "number") return value;
    if (typeof value === "string" && breakpoints[value] != null)
        return breakpoints[value];
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

// Enhanced hook: accepts either a named breakpoint (e.g., "sm"),
// a raw media query string (e.g., "(max-width: 640px)"), or an object like { min: "md" } / { max: "md" }.
// defaultMatch mirrors the prior useMediaQuery API for SSR-safe defaults.
function useBreakpoint(input, defaultMatch = false) {
    const [matches, setMatches] = useState(defaultMatch);

    useEffect(() => {
        // SSR guard
        if (typeof window === "undefined") return;

        // Build media query string from the input
        let query = "";

        if (!input) return;

        if (typeof input === "string") {
            const str = input.trim();
            // If it looks like a raw media query, use it directly
            if (str.startsWith("(") || /min-width|max-width/.test(str)) {
                query = str;
            } else if (breakpoints[str] != null) {
                // Named breakpoint implies max-width by default, matching prior behavior
                const maxPx = breakpoints[str] - 1;
                query = `(max-width: ${maxPx}px)`;
            } else {
                // Fallback: treat unknown strings as raw queries
                query = str;
            }
        } else if (typeof input === "object") {
            if (input.min != null) {
                const min = resolveBreakpointValue(input.min);
                if (min != null) query = `(min-width: ${min}px)`;
            }
            if (!query && input.max != null) {
                const maxRaw = resolveBreakpointValue(input.max);
                if (maxRaw != null) {
                    const max =
                        typeof input.max === "string" ? maxRaw - 1 : maxRaw - 1;
                    query = `(max-width: ${max}px)`;
                }
            }
        }

        if (!query) return;

        try {
            const mediaQueryList = window.matchMedia(query);
            const updateMatch = (e) => setMatches(e.matches);
            // Initialize
            setMatches(mediaQueryList.matches);
            // Subscribe
            mediaQueryList.addEventListener("change", updateMatch);
            return () =>
                mediaQueryList.removeEventListener("change", updateMatch);
        } catch (_) {
            // In environments without matchMedia, keep default state
        }
    }, [input, defaultMatch]);

    return matches;
}

export default useBreakpoint;
